import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Home } from 'lucide-react';

const TermsOfService = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    document.title = 'Terms of Service - AUMONLINE Financial Planner';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-green-50">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-8">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="flex items-center gap-2 border-2 border-blue-800 text-blue-800 hover:bg-blue-800 hover:text-white transition-colors"
            >
              <Home className="w-5 h-5" />
              Back to Home
            </Button>
            
            <div className="flex flex-col items-center gap-4">
              <a href="https://www.aumonline.in" target="_blank" rel="noopener noreferrer">
                <img 
                  src="https://customer-assets.emergentagent.com/job_retire-planner-2/artifacts/5j61poys_Logo.jpg" 
                  alt="AUMONLINE Logo" 
                  className="h-16 object-contain hover:opacity-80 transition-opacity duration-200 cursor-pointer"
                />
              </a>
            </div>
            
            <div className="w-32"></div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-xl p-8 md:p-12">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Terms of Service</h1>
          <p className="text-gray-600 mb-8">Effective Date: December 15th, 2025</p>

          <div className="prose max-w-none space-y-6 text-gray-700">
            <p>
              Welcome to AUMONLINE Financial Planner. These Terms of Service ("Terms") govern your use of our website and services located at https://planner.aumonline.in/ (the "Service"). By accessing or using the Service, you agree to be bound by these Terms.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">1. Acceptance of Terms</h2>
            <p>
              By accessing and using the AUMONLINE Financial Planner service, you accept and agree to be bound by the terms and provisions of this agreement. If you do not agree to these Terms, please do not use our Service.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">2. Description of Service</h2>
            <p>
              AUMONLINE Financial Planner provides online financial planning tools including:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Retirement Cash Flow Planning Calculator</li>
              <li>Children's Future Education Fund Planning Calculator</li>
              <li>Systematic Withdrawal Plan (SWP) Calculator</li>
            </ul>
            <p>
              These tools are provided for informational and planning purposes only. The calculations and projections are estimates based on the inputs you provide and should not be considered as financial advice.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">3. Disclaimer</h2>
            <p>
              <strong>Important:</strong> The information and calculators provided by AUMONLINE Financial Planner are for educational and illustrative purposes only. They do not constitute financial, investment, tax, or legal advice. 
            </p>
            <p>
              <strong>Investment Risks:</strong> Mutual fund investments are subject to market risks. Please read all scheme-related documents carefully before investing. Past performance is not indicative of future results.
            </p>
            <p>
              We strongly recommend consulting with qualified financial advisors, tax professionals, or legal experts before making any financial decisions based on the calculations or information provided through our Service.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">4. User Responsibilities</h2>
            <p>When using our Service, you agree to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide accurate and truthful information when using our calculators</li>
              <li>Use the Service only for lawful purposes</li>
              <li>Not attempt to gain unauthorized access to any part of the Service</li>
              <li>Not transmit any viruses, malware, or harmful code</li>
              <li>Not use the Service to violate any applicable laws or regulations</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">5. Intellectual Property</h2>
            <p>
              All content on the AUMONLINE Financial Planner website, including but not limited to text, graphics, logos, images, software, and calculators, is the property of AUMONLINE or its content suppliers and is protected by Indian and international copyright laws.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">6. Privacy Policy</h2>
            <p>
              Your use of the Service is also governed by our Privacy Policy. Please review our <button onClick={() => navigate('/privacy-policy')} className="text-blue-600 hover:underline">Privacy Policy</button> to understand our practices.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">7. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by applicable law, AUMONLINE Financial Planner shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Your use or inability to use the Service</li>
              <li>Any financial decisions made based on the calculations or information provided</li>
              <li>Any errors or omissions in the Service</li>
              <li>Any unauthorized access to or use of our servers and/or any personal information stored therein</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">8. Modifications to Service</h2>
            <p>
              We reserve the right to modify or discontinue, temporarily or permanently, the Service (or any part thereof) with or without notice. You agree that AUMONLINE shall not be liable to you or any third party for any modification, suspension, or discontinuance of the Service.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">9. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of India, without regard to its conflict of law provisions. Any disputes arising under or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts located in Mysore, Karnataka, India.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">10. SEBI Compliance</h2>
            <p>
              <strong>Regulatory Disclaimer:</strong> Mutual Fund investments are subject to market risks. Read all scheme-related documents carefully before investing. The calculations provided are estimates and projections based on assumed rates of return and do not guarantee actual returns.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">11. Changes to Terms</h2>
            <p>
              We reserve the right to update or modify these Terms at any time without prior notice. Your continued use of the Service after any such changes constitutes your acceptance of the new Terms. We encourage you to review these Terms periodically.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">12. Contact Information</h2>
            <p>If you have any questions about these Terms, please contact us:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Company:</strong> AUMONLINE</li>
              <li><strong>Address:</strong> B-401, Vaishnavi Sarovar, Yadavgiri, Mysore, India - 570020</li>
              <li><strong>Email:</strong> <a href="mailto:ashwath@aumonline.in" className="text-blue-600 hover:underline">ashwath@aumonline.in</a></li>
              <li><strong>Phone:</strong> <a href="tel:+918217589700" className="text-blue-600 hover:underline">+918217589700</a></li>
              <li><strong>Website:</strong> <a href="https://planner.aumonline.in" className="text-blue-600 hover:underline">https://planner.aumonline.in</a></li>
            </ul>

            <div className="mt-8 p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded">
              <p className="font-semibold text-gray-800">Important Notice:</p>
              <p className="text-sm mt-2">
                By using AUMONLINE Financial Planner, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. If you do not agree to these terms, you must discontinue use of the Service immediately.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-gray-600">
          <p className="text-sm">
            © 2025 AUMONLINE. All rights reserved. | 
            <button onClick={() => navigate('/privacy-policy')} className="text-blue-600 hover:underline ml-2">Privacy Policy</button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
